import { useEffect, useState } from "react";
import { X, Target, CheckCircle2, Sparkles, Clock } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Daily Quests — 3 rotating tasks per user per UTC day. Rewards drop into `souls`
 * automatically on completion (server-side). Same visitor sees the SAME 3 quests
 * all day, so bookmarks + push notifications reference the same goal.
 */
export default function DailyQuestsModal({ open, onClose, onSoulsChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState("");

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/quests/daily");
        if (alive) setData(r);
      } catch {
        if (alive) setData({ quests: [], completed: 0, total: 0 });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  // Live countdown to next reset — the clock stays warm even between opens.
  useEffect(() => {
    if (!open || !data?.resets_at) return;
    const tick = () => {
      const ms = new Date(data.resets_at).getTime() - Date.now();
      if (ms <= 0) { setCountdown("resetting..."); return; }
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setCountdown(`${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [open, data?.resets_at]);

  if (!open) return null;

  const quests = data?.quests || [];
  const completed = data?.completed || 0;
  const total = data?.total || 0;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ background: "rgba(4,2,1,0.85)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="quests-modal-backdrop"
    >
      <div
        className="panel w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        style={{
          background: "radial-gradient(ellipse at top, rgba(90,60,10,0.3), rgba(8,4,2,0.98))",
          borderColor: "rgba(212,175,55,0.55)",
          boxShadow: "0 0 40px rgba(212,175,55,0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="quests-modal"
      >
        <div className="flex items-start justify-between p-5 pb-3 border-b border-white/10">
          <div>
            <div className="text-[10px] tracking-[0.4em] gold-text">— DAILY PACTS —</div>
            <div className="font-heading text-3xl italic text-[#f0e6d2] mt-1">Bounties of the Ember</div>
            <div className="mt-1 flex items-center gap-1 text-[10px] tracking-widest ash-text">
              <Clock size={11} /> RESETS IN <span className="gold-text tabular-nums">{countdown || "…"}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="font-heading text-3xl gold-text" data-testid="quests-completion">
                {completed}/{total}
              </div>
              <div className="text-[10px] tracking-widest ash-text">CLAIMED</div>
            </div>
            <button
              onClick={onClose}
              className="panel p-2 hover:border-[#d4af37] transition-colors"
              data-testid="quests-close-btn"
            >
              <X size={16} className="ash-text" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          {loading && (
            <div className="text-center ash-text italic py-12">The Ember whispers its wagers...</div>
          )}
          {!loading && quests.map((q) => {
            const pct = Math.min(100, (q.progress / q.target) * 100);
            const done = !!q.completed;
            return (
              <div
                key={q.id}
                className={`panel p-4 ${done ? "border-[#d4af37]/70" : "border-white/10"}`}
                style={done ? { background: "linear-gradient(90deg, rgba(212,175,55,0.08), transparent)" } : {}}
                data-testid={`quest-${q.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {done
                        ? <CheckCircle2 size={14} className="gold-text" />
                        : <Target size={14} className="ash-text" />}
                      <span className={`font-heading text-lg italic ${done ? "gold-text" : "text-[#f0e6d2]"}`}>
                        {q.title}
                      </span>
                    </div>
                    <div className="text-[11px] font-narrative ash-text italic mt-0.5">{q.desc}</div>
                  </div>
                  <div
                    className={`text-right flex-shrink-0 ${done ? "gold-text" : "ash-text"}`}
                    data-testid={`quest-reward-${q.id}`}
                  >
                    <div className="flex items-center gap-1 font-heading text-2xl">
                      <Sparkles size={12} /> {q.reward}
                    </div>
                    <div className="text-[9px] tracking-widest">SOULS</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-1.5 bg-black/60 border border-white/10 overflow-hidden">
                    <div
                      className="h-full transition-all duration-700"
                      style={{
                        width: `${pct}%`,
                        background: done
                          ? "linear-gradient(90deg, #d4af37, #fff2b0)"
                          : "linear-gradient(90deg, #ff6b3a, #8a0303)",
                        boxShadow: done
                          ? "0 0 10px rgba(212,175,55,0.5) inset"
                          : "0 0 8px rgba(226,88,34,0.35) inset",
                      }}
                    />
                  </div>
                  <div
                    className={`text-[11px] tabular-nums tracking-widest ${done ? "gold-text" : "ash-text"} flex-shrink-0`}
                    data-testid={`quest-progress-${q.id}`}
                  >
                    {q.progress} / {q.target}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-3 border-t border-white/10 text-center text-[10px] tracking-[0.3em] ash-text">
          Rewards drop into your Souls the moment a pact completes.
        </div>
      </div>
    </div>
  );
}
